import { useEffect, useState } from "react";
import { solanaPubkeyBase58 } from "@stealthos/sdk";
import {
  normalizeHandle,
  resolveStealthHandle,
} from "../../utils/handle-resolver";
import { solanaRecipientInboxKey } from "../../joinsplit-v2/solana/recipient";

export function isSolanaHandleInput(input: string): boolean {
  const value = input.trim();
  if (value.toLowerCase().endsWith(".stealth")) return true;
  // Public wallet addresses take precedence over suffix-free handle names.
  try {
    solanaPubkeyBase58(value, "recipient");
    return false;
  } catch {
    return normalizeHandle(value) !== null;
  }
}

export async function resolveSolanaHandleRecipient(
  input: string,
  network: string,
  signal?: AbortSignal,
) {
  const handle = normalizeHandle(input);
  if (!handle || !isSolanaHandleInput(input))
    throw new Error("Enter a valid .stealth handle");
  const record = await resolveStealthHandle(handle, "base", {
    network,
    signal,
  });
  if (
    !record.mpkHex ||
    !record.inboxPublicKeyHex ||
    !solanaRecipientInboxKey(record.inboxPublicKeyHex)
  )
    throw new Error(
      "This handle has no valid Solana private receiving address",
    );
  return `${record.mpkHex}:${record.inboxPublicKeyHex}`;
}

/** Never expose a previous lookup after the input/network changes. */
export function useSolanaHandleRecipient(input: string, network: string) {
  const name = normalizeHandle(input) ?? input.trim().toLowerCase();
  const enabled = isSolanaHandleInput(input);
  const key = `${network}:${name}`;
  const [result, setResult] = useState<{
    key: string;
    address?: string;
    error?: string;
  } | null>(null);
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      void resolveSolanaHandleRecipient(name, network, controller.signal)
        .then((address) => {
          if (!controller.signal.aborted) setResult({ key, address });
        })
        .catch((error) => {
          if (!controller.signal.aborted)
            setResult({
              key,
              error:
                error instanceof Error
                  ? error.message
                  : "Could not verify handle",
            });
        });
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [enabled, key, name, network]);
  const current = enabled && result?.key === key ? result : null;
  return {
    address: current?.address ?? null,
    error: current?.error ?? null,
    loading: enabled && !current,
    enabled,
  };
}
